import { Form, Input, Modal } from "antd"
import { Button } from "components"
import { PASSWORD_REGEX } from "utils/regex"
import { AppOTPInput } from "components/inputs/AppOTPInput"
import { ZIndex } from "constants/zIndex"
import { useEffect, useState } from "react"
import { ILoginWith2FA } from "stores/auth"

type Props = {
  loginResponse: ILoginWith2FA
  completePasswordReset: (input: {
    code: string
    currentPassword: string
    newPassword: string
  }) => void
  isSubmitting: boolean
  resendCode: () => void
  isResending: boolean
  onDismiss: () => void
}

type FormValues = {
  currentPassword: string
  password: string
  cpassword: string
}

export const ResetPasswordPrompt = ({
  loginResponse,
  completePasswordReset,
  isSubmitting,
  resendCode,
  isResending,
  onDismiss,
}: Props) => {
  const [otp, setOtp] = useState("")
  const [timer, setTimer] = useState(30)
  const [form] = Form.useForm<FormValues>()

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const handleResend = () => {
    resendCode()
    setTimer(30)
    setOtp("")
  }

  const onFinish = (values: FormValues) => {
    completePasswordReset({
      code: otp,
      currentPassword: values.currentPassword,
      newPassword: values.password,
    })
  }

  const otpInvalid = !otp || otp.length < 6

  return (
    <Modal
      open
      footer={null}
      onCancel={onDismiss}
      zIndex={ZIndex.Prompt}
      className="lg:max-w-lg max-w-sm"
    >
      <>
        <div className="bg-white flex flex-col items-center gap-4 w-full">
          <div className="text-xl font-circular font-semibold text-padeBlack">
            Update your password
          </div>
          <div className="text-sm text-center text-padeSubtext font-avenir">
            {loginResponse?.message ||
              "Please enter the one-time verification code sent to your email and choose a new password to continue."}
          </div>

          <div className="w-full px-4">
            {/* Not submitted on the last digit: the new password is still to come below. */}
            <AppOTPInput
              otp={otp}
              setOtp={setOtp}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex gap-2 text-sm">
            <span className="text-padeSubtext font-avenir">
              Didn't receive the code?
            </span>
            <span
              className={`font-semibold font-circular ${
                timer === 0
                  ? "cursor-pointer text-primary500 underline"
                  : "cursor-auto"
              }`}
              onClick={() => {
                if (timer === 0) handleResend()
              }}
            >
              {isResending
                ? "Resending…"
                : timer > 0
                ? `Resend in ${timer}s`
                : "Resend"}
            </span>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="w-full"
            requiredMark={false}
          >
            <Form.Item
              name="currentPassword"
              hasFeedback
              rules={[
                {
                  required: true,
                  message: "Please input your current password!",
                },
              ]}
              label={
                <span className="font-avenir font-medium text-[14px] text-neutral400">
                  Current Password
                </span>
              }
            >
              <Input.Password className="h-10" />
            </Form.Item>

            <Form.Item
              name="password"
              dependencies={["currentPassword"]}
              hasFeedback
              rules={[
                { required: true, message: "Please input your password!" },
                {
                  pattern: PASSWORD_REGEX,
                  message:
                    "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("currentPassword") !== value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error(
                        "New password must be different from your current password"
                      )
                    )
                  },
                }),
              ]}
              label={
                <span className="font-avenir font-medium text-[14px] text-neutral400">
                  New Password
                </span>
              }
            >
              <Input.Password className="h-10" />
            </Form.Item>

            <Form.Item
              name="cpassword"
              dependencies={["password"]}
              hasFeedback
              rules={[
                { required: true, message: "Please confirm your password!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error(
                        "The two passwords that you entered do not match!"
                      )
                    )
                  },
                }),
              ]}
              label={
                <span className="font-avenir font-medium text-[14px] text-neutral400">
                  Confirm Password
                </span>
              }
            >
              <Input.Password className="h-10" />
            </Form.Item>

            <div className="flex justify-center mt-2">
              <Button
                color="primary"
                type="submit"
                disabled={otpInvalid || isSubmitting}
              >
                {isSubmitting ? "Updating…" : "Update password"}
              </Button>
            </div>
          </Form>
        </div>
      </>
    </Modal>
  )
}
